import { useEffect, useRef, useState } from "react";
import { useLazyLookupRfidTagQuery } from "../../../../../redux/api/ev-charger/evChargerAPI";
import { useNfcScanner } from "../../../../../shared/hooks/useNfcScanner";
import IValidateOcppTagResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/validate-ocpp-tag-response-dto";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import { showErrorMessage } from "../../../../../shared/oversight-core/utils/toast";
import styles from "./scan-rfid-modal.module.scss";

interface IProps extends ModelContainerProps {
  show: boolean;
  onScanSuccess: (scannedTagId: string) => void;
}

const ScanRfidModal = (props: IProps) => {
  const { show, onScanSuccess, onClose, onCancel, ...rest } = props;

  const { status, scannedCard, errorMessage, startScan, resetScan } =
    useNfcScanner();

  const [triggerLookup] = useLazyLookupRfidTagQuery();

  const [isVerifying, setIsVerifying] = useState(false);
  const [tagInfo, setTagInfo] = useState<IValidateOcppTagResponseDTO | null>(
    null,
  );

  // Track whether the success callback has already fired for the current scan
  const successFiredRef = useRef(false);

  // Start / stop scanning when modal opens / closes
  useEffect(() => {
    if (show) {
      successFiredRef.current = false;
      setIsVerifying(false);
      setTagInfo(null);
      startScan();
    } else {
      resetScan();
      setIsVerifying(false);
      setTagInfo(null);
    }
  }, [show]);

  // Backend verification step after NFC scan read
  useEffect(() => {
    if (status !== "success" || !scannedCard || successFiredRef.current) return;

    successFiredRef.current = true;
    setIsVerifying(true);

    triggerLookup({ idTag: scannedCard.serialNumber })
      .unwrap()
      .then((res: IValidateOcppTagResponseDTO) => {
        setTagInfo(res);
      })
      .catch((err: IHttpError) => {
        showErrorMessage(
          err.message || "RFID Backend lookup skipped or failed",
        );
      })
      .finally(() => {
        setIsVerifying(false);
        const timer = setTimeout(() => {
          onScanSuccess(scannedCard.serialNumber);
          handleResetAndClose();
        }, 1200);
        return () => clearTimeout(timer);
      });
  }, [status, scannedCard]);

  const handleResetAndClose = () => {
    resetScan();
    setIsVerifying(false);
    setTagInfo(null);
    if (onClose) onClose();
    if (onCancel) onCancel();
  };

  // Map hook status to the display status used in UI
  const displayStatus =
    status === "idle" ? "scanning" : status === "timeout" ? "error" : status;

  const displayError =
    status === "timeout"
      ? "Scan timed out. No RFID tag was detected. Please try again."
      : errorMessage;

  return (
    <ModalContainer
      show={show}
      {...rest}
      title="Scan RFID Tag"
      onClose={handleResetAndClose}
      onCancel={handleResetAndClose}
      cancelButtonText="Cancel"
      cancelButtonVariant="blue"
      size="modal-md"
      hideConfirm={true}
    >
      <div className={styles.scannerContainer}>
        {displayStatus === "scanning" && !isVerifying && (
          <>
            <div className={styles.radarWrapper}>
              <div className={styles.radarCircle} />
              <div
                className={`${styles.radarCircle} ${styles.radarCircleDelay}`}
              />
              <div className={styles.radarCenterIcon}>
                <MaterialIcon icon="sensors" color="#7483CC" size={32} />
              </div>
            </div>
            <div className={styles.instructionText}>
              Ready to Scan Physical RFID Card
            </div>
            <div className={styles.helperText}>
              Hold your physical RFID card near the back of your phone / mobile
              device to scan.
            </div>
          </>
        )}

        {isVerifying && (
          <>
            <div className={styles.radarWrapper}>
              <div className={styles.radarCenterIcon}>
                <MaterialIcon icon="cached" color="#7483CC" size={32} />
              </div>
            </div>
            <div className={styles.instructionText}>Verifying RFID Tag...</div>
            <div className={styles.helperText}>
              Checking card details and registration status...
            </div>
          </>
        )}

        {displayStatus === "success" && !isVerifying && scannedCard && (
          <>
            <div className={styles.radarWrapper}>
              <div
                className={styles.radarCenterIcon}
                style={{ backgroundColor: "var(--success)" }}
              >
                <MaterialIcon icon="check" color="#ffffff" size={36} />
              </div>
            </div>
            <div className={styles.instructionText}>
              {tagInfo?.holderName
                ? `Registered Tag Found: ${tagInfo.holderName}`
                : "RFID Tag Scanned!"}
            </div>
            <div className={styles.scannedBox}>
              <MaterialIcon icon="credit_card" color="#7483CC" size={20} />
              <span className={styles.scannedTagText}>
                {scannedCard.serialNumber}
              </span>
            </div>
          </>
        )}

        {displayStatus === "unsupported" && (
          <>
            <div className={styles.radarWrapper}>
              <div className={styles.radarCenterIcon}>
                <MaterialIcon icon="smartphone" color="#69768b" size={32} />
              </div>
            </div>
            <div className={styles.instructionText}>Web NFC Unsupported</div>
            <div className={styles.helperText}>
              Web NFC scanning is supported on{" "}
              <strong>Google Chrome on Android</strong> over HTTPS. On desktop,
              connect a USB/Bluetooth RFID reader to auto-type tag IDs.
            </div>
          </>
        )}

        {displayStatus === "error" && (
          <>
            <div className={styles.radarWrapper}>
              <div
                className={styles.radarCenterIcon}
                style={{ backgroundColor: "var(--danger)" }}
              >
                <MaterialIcon icon="error_outline" color="#ffffff" size={32} />
              </div>
            </div>
            <div className={styles.instructionText}>Scanning Failed</div>
            <div className={styles.helperText}>
              {displayError || "Unable to read RFID tag. Please try again."}
            </div>
          </>
        )}
      </div>
    </ModalContainer>
  );
};

export default ScanRfidModal;
